import { SortDirection } from "@tanstack/react-table";
import {
  ChevronDown,
  FolderIcon,
  Loader,
  PenLine,
  PlusIcon,
  Trash2,
  UploadIcon,
  X,
} from "lucide-react";
import { type ComponentProps, ReactNode, forwardRef } from "react";

import { Button } from "./button";

import { cn } from "@/lib/utils";

type TProps = ComponentProps<typeof Button> & {
  icon?:
    | "add"
    | "export"
    | "header"
    | "table-export"
    | "delete"
    | "loading"
    | React.ReactNode;
  iconConfig?: {
    position: "left" | "right";
    className?: string;
  };
  reverse?: boolean;
  sort?: boolean | SortDirection;
};

const ButtonWithIcon = forwardRef<HTMLButtonElement, TProps>(
  (
    {
      icon = "add",
      iconConfig = {
        position: "left",
      },
      sort,
      reverse,
      ...props
    },
    ref,
  ) => {
    const ico = (): ReactNode => {
      switch (icon) {
        case "export":
          return <UploadIcon size={18} className='mr-3' />;
        case "delete":
          return <Trash2 size={18} className='mr-3' />;
        case "table-export":
          return (
            <UploadIcon
              size={18}
              className='sm:mr-0 xl:mr-3 size-4 3xl:size-5'
            />
          );
        case "header":
          return (
            <ChevronDown
              className={cn(
                "ml-2 size-4 transition-all ease-in-out",
                sort === "asc" && "rotate-180",
              )}
            />
          );
        case "add":
          return <PlusIcon className='mr-2 size-4 3xl:size-5' id='plus-icon' />;
        case "folder":
          return <FolderIcon className='mr-2 size-4 3xl:size-5' />;
        case "cancel":
          return <X className='mr-2 size-4 3xl:size-5' />;
        case "edit":
          return <PenLine className='mr-2 size-4 3xl:size-5' />;
        case "loading":
          return <Loader className='mr-2 size-4 3xl:size-5' />;
        default:
          return icon;
      }
    };

    return (
      <Button
        {...props}
        ref={ref}
        className={cn(props.className, reverse && "flex-row-reverse")}
      >
        {iconConfig.position === "left" && ico()}

        {props.children}
        {iconConfig.position === "right" && ico()}
      </Button>
    );
  },
);

ButtonWithIcon.displayName = "ButtonWithIcon";

export default ButtonWithIcon;
